import React, { useCallback, useContext, useMemo } from "react";
import { HeaderA } from "components/headers";
import { ModuleWrapper } from "components/fragments";
import { DataTable, Pill } from "components/commons";
import { useApi, useFilter, useMount, useRouter } from "hooks";
import { searchKds } from "apis";
import { kdsListResponse, kdsListFilterRequest } from "mappers";
import { mapObject } from "services";
import { Path } from "paths";
import { VenueContext } from "contexts";
import { PillType } from "enums";
import { Popover } from "antd";
import classnames from "classnames";
import { v4 as uuidv4 } from "uuid";
import KdsFilter from "./kds-filter";

const columns = [
  { key: "name", text: "Name" },
  { key: "outletName", text: "Outlet" },
  { key: "type", text: "Type" },
  { key: "status", text: "Status", custom: true },
  { key: "isDefault", text: "Default", custom: true },
  { key: "action", align: "right" },
];

const statusPillType = {
  ACTIVE: PillType.Green,
  INACTIVE: PillType.Opaque,
};

const KdsList = () => {
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;
  const { history } = useRouter();

  const {
    request,
    loading,
    error,
    result = { data: [], metadata: { total: 0 } },
    mappedData = [],
  } = useApi({
    api: searchKds,
    isArray: true,
    mapper: kdsListResponse,
  });

  const defaultFilter = {
    venueId,
    outletId: null,
    status: null,
    searchKey: "",
    page: 1,
    pageSize: 20,
  };

  const { modifyFilter, modifyFilters, filterState, requestState, clearFilter } = useFilter(
    defaultFilter,
    defaultFilter
  );

  useMount(() => {
    fetchKds(requestState);
  });

  const fetchKds = useCallback(
    async (rs) => {
      const params = mapObject(rs, kdsListFilterRequest);
      if (!params.outletId) delete params.outletId;
      if (!params.status) delete params.status;
      if (!params.searchKey) delete params.searchKey;
      await request(params);
    },
    [request]
  );

  const fetchUpdateStore = useCallback(
    async (rs) => {
      fetchKds(rs);
    },
    [fetchKds]
  );

  const renderActionOptions = useCallback(
    (row) => {
      const options = [
        {
          text: "Edit",
          onClick: () =>
            history.push({
              pathname: Path.LOCATION_KDS_EDIT(row.id),
              state: { kdsData: row },
            }),
        },
      ];

      return (
        <Popover
          overlayClassName="options-popover"
          placement="bottomRight"
          content={options.map((option) => (
            <button
              key={uuidv4()}
              className={classnames("border-none outline-none w-full text-left", option.className)}
              onClick={option.onClick}
            >
              {option.text}
            </button>
          ))}
          trigger="click"
          arrow={false}
        >
          <div className="text-right">
            <i className="icon-options align-middle" style={{ fontSize: 3 }} />
          </div>
        </Popover>
      );
    },
    [history]
  );

  const data = useMemo(
    () =>
      mappedData.map((row) => ({
        ...row,
        status: (
          <Pill size="text-xs" type={statusPillType[row.status] || PillType.Opaque}>
            {row.status}
          </Pill>
        ),
        isDefault: (
          <Pill type={row.isDefault ? PillType.Green : PillType.Opaque}>
            {row.isDefault ? "Yes" : "No"}
          </Pill>
        ),
        action: renderActionOptions(row),
      })),
    [mappedData, renderActionOptions]
  );

  return (
    <ModuleWrapper
      header={
        <HeaderA
          title="KDS"
          description="Manage kitchen display systems by outlet"
          button={{ path: Path.LOCATION_KDS_CREATE, text: "Add New KDS" }}
          className="mb-md"
        />
      }
    >
      <KdsFilter
        filterState={filterState}
        modifyFilter={modifyFilter}
        clearFilter={clearFilter}
        fetchList={fetchUpdateStore}
        onApply={(searchKey) => {
          const { requestState: rs } = modifyFilters({ page: 1, searchKey });
          fetchUpdateStore(rs);
        }}
      />
      <DataTable
        page={filterState.page}
        pageSize={filterState.pageSize}
        onChangePage={modifyFilters}
        fetchList={fetchUpdateStore}
        total={result.metadata?.total}
        loading={loading}
        error={error}
        columns={columns}
        data={data}
      />
    </ModuleWrapper>
  );
};

export default KdsList;
